// src/pages/Account.js
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { LogOut, CreditCard, User, BarChart2, Share2, Building, Key } from 'lucide-react';
import { getCurrentUserId } from '../services/database';
import proxyapi from '../services/ProxyApi';
import Navbar from '../components/Navbar';
import UpdatePassword from '../components/UpdatePassword';
import UpdateEmail from '../components/UpdateEmail';
import UserStats from '../components/UserStats';
import UserStats2 from '../components/UserStats2';
import ManageShares from '../components/ManageShares';
import { useAuth } from '../hooks/useAuth';
import { getUserEnterpriseStatus } from '../services/enterprise';
import EnterpriseAdmin from '../components/EnterpriseAdmin';
import EnterpriseJoin from '../components/EnterpriseJoin';
// HYBRID VIEW IMPORTS: Three specialized components
import PassStatus from '../components/PassStatus';
import EnterprisePassStatus from '../components/EnterprisePassStatus';
import EnterpriseAdminPassStatus from '../components/EnterpriseAdminPassStatus';

function Account() {
  const navigate = useNavigate();
  const location = useLocation();
  const [subscription, setSubscription] = useState(null);
  const [payments, setPayments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('access-pass');
  const [enterpriseStatus, setEnterpriseStatus] = useState(null);
  const [showJoinEnterprise, setShowJoinEnterprise] = useState(false);
  // TODO: Re-enable when enterprise payments endpoint is implemented
  // const [enterprisePayments, setEnterprisePayments] = useState([]);
  const { logout } = useAuth();

  // ADDED: Refs to track if we should restore scroll position
  const shouldRestoreScrollRef = useRef(false);
  const savedScrollPositionRef = useRef(0);

  // Set the active tab from location state, if provided
  useEffect(() => {
    if (location.state?.activeTab) {
      setActiveTab(location.state.activeTab);
    }

    // ADDED: Check if we need to restore scroll position
    if (location.state?.preserveState && location.state?.scrollPosition !== undefined) {
      shouldRestoreScrollRef.current = true;
      savedScrollPositionRef.current = location.state.scrollPosition;
    }
  }, [location.state]);

  // ADDED: Restore scroll position after component loads and tab is set
  useEffect(() => {
    if (shouldRestoreScrollRef.current && !isLoading) {
      // Use setTimeout to ensure the DOM is fully rendered
      setTimeout(() => {
        window.scrollTo(0, savedScrollPositionRef.current);
        shouldRestoreScrollRef.current = false;
      }, 50);
    }
  }, [isLoading, activeTab]);

  // ADDED: Save scroll position when navigating away from enterprise tab
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (activeTab === 'enterprise') {
        sessionStorage.setItem('enterpriseTabScrollPosition', window.pageYOffset.toString());
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [activeTab]);

  useEffect(() => {
    const fetchSubscriptionAndEnterprise = async () => {
      try {
        setIsLoading(true);
        const userId = await getCurrentUserId();
        if (!userId) {
          navigate('/login');
          return;
        }

        // Fetch user subscription and enterprise status in parallel
        const [subscriptionResponse, enterpriseStatusResponse] = await Promise.all([
          proxyapi.get(`/api/v1/users/${userId}/subscription`),
          getUserEnterpriseStatus()
        ]);

        if (subscriptionResponse.data) {
          setSubscription({
            status: subscriptionResponse.data.status,
            expiresAt: subscriptionResponse.data.expiresAt,
            trialGenerationsLeft: subscriptionResponse.data.trialGenerationsLeft,
            timeRemaining: subscriptionResponse.data.timeRemaining,
          });

          // Set payment history
          if (subscriptionResponse.data.payments) {
            setPayments(subscriptionResponse.data.payments);
          }
        } else {
          setError('Could not retrieve access pass information');
        }
        
        // Set enterprise status
        if (enterpriseStatusResponse && enterpriseStatusResponse.status === 'success') {
          setEnterpriseStatus(enterpriseStatusResponse);
          
          // TODO: Enterprise payments endpoint not yet implemented
          // Commented out until backend endpoint is created
          /*
          if (enterpriseStatusResponse.isEnterpriseAdmin && enterpriseStatusResponse.enterpriseId) {
            try {
              const enterprisePaymentsResponse = await proxyapi.get(`/api/v1/enterprise/${enterpriseStatusResponse.enterpriseId}/payments`);
              setEnterprisePayments(enterprisePaymentsResponse.data.payments || []);
            } catch (enterprisePaymentError) {
              console.error('Error fetching enterprise payments:', enterprisePaymentError);
              setEnterprisePayments([]);
            }
          }
          */
        } else {
          // Set default enterprise status if the response is invalid or unsuccessful
          setEnterpriseStatus({
            status: 'success',
            isEnterpriseMember: false
          });
        }
        
      } catch (error) {
        console.error('Error fetching account info:', error);
        setError('Could not retrieve account information. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchSubscriptionAndEnterprise();
  }, [navigate]);

  const handleLogout = async () => {
    try {
      // This will trigger the broadcast to other tabs via the auth service
      await logout();
      // The navigation to login page is already handled in the logout function
      // No need to navigate here as it's handled in the useAuth hook
    } catch (error) {
      console.error('Error signing out:', error);
      // Show error notification to user
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  // REMOVED: AccessPassStatus component - now handled by PassStatus component

  // Render content based on active tab
  const renderTabContent = () => {
    if (isLoading) {
      return (
        <div className="bg-white shadow rounded-lg p-6 mb-6 flex justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="bg-white shadow rounded-lg p-6 mb-6">
          <div className="text-red-500">{error}</div>
        </div>
      );
    }

    switch (activeTab) {
      case 'access-pass':
        // HYBRID VIEW: More precise logic for each user type
        if (enterpriseStatus?.isEnterpriseMember && enterpriseStatus?.isEnterpriseAdmin) {
          // Enterprise Admin: Shows enterprise status + enterprise management options
          return (
            <>
              <EnterprisePassStatus enterpriseStatus={enterpriseStatus} />
              <EnterpriseAdminPassStatus />
            </>
          );
        } else if (enterpriseStatus?.isEnterpriseMember) {
          // Regular Enterprise User: Shows only enterprise status (clean, simple)
          return <EnterprisePassStatus enterpriseStatus={enterpriseStatus} />;
        } else if (enterpriseStatus?.isRevokedEnterpriseMember) {
          // REVOKED ENTERPRISE USER: Use the new flag to detect revoked users
          return <EnterprisePassStatus enterpriseStatus={enterpriseStatus} />;
        } else {
          // Non-Enterprise Users: Shows personal subscription + payments
          return <PassStatus 
            subscription={subscription} 
            payments={payments}
          />;
        }

      case 'usage':
        return (
          <>
            <UserStats />
            <UserStats2 />
          </>
        );

      case 'shares':
        return <ManageShares />;

      case 'enterprise':
        if (isLoading) {
          return (
            <div className="bg-white shadow rounded-lg p-6 mb-6 flex justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
            </div>
          );
        }

        if (enterpriseStatus?.isEnterpriseMember && enterpriseStatus?.isEnterpriseAdmin) {
          // Show enterprise admin panel
          return <EnterpriseAdmin enterpriseId={enterpriseStatus.enterpriseId} />;
        } else if (enterpriseStatus?.isEnterpriseMember) {
          // Show enterprise member info
          return (
            <div className="bg-white shadow rounded-lg p-6 mb-6">
              <h2 className="text-xl font-semibold mb-4 flex items-center">
                <Building className="mr-2" size={20} />
                Enterprise Membership
              </h2>

              <div className="bg-blue-50 p-4 rounded-md mb-4">
                <p className="text-blue-800">
                  You are a member of <strong>{enterpriseStatus.enterpriseName}</strong>
                </p>
                <p className="text-sm text-blue-600 mt-2">
                  Your access is managed by your enterprise administrator.
                </p>
              </div>

              <div className="mt-4">
                <p className="text-gray-600">
                  Enterprise status:
                  <span className={`ml-2 px-2 py-1 text-xs font-medium rounded-full ${
                    enterpriseStatus.enterpriseStatus === 'active'
                      ? 'bg-green-100 text-green-800'
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {enterpriseStatus.enterpriseStatus === 'active' ? 'Active' : 'Expired'}
                  </span>
                </p>
                <p className="text-gray-600 mt-2">
                  Expires: {formatDate(enterpriseStatus.enterpriseExpiresAt)}
                </p>
              </div>
            </div>
          );
        } else {
          // Show enterprise join option
          return (
            <div className="space-y-6">
              <div className="bg-white shadow rounded-lg p-6 mb-6">
                <h2 className="text-xl font-semibold mb-4 flex items-center">
                  <Building className="mr-2" size={20} />
                  Enterprise Access
                </h2>

                <p className="text-gray-600 mb-4">
                  Join your organization's enterprise account to get access to features managed by your team administrator.
                </p>

                {showJoinEnterprise ? (
                  <EnterpriseJoin
                    onSuccess={(result) => {
                      // Refresh enterprise status after joining
                      window.location.reload();
                    }}
                    onCancel={() => setShowJoinEnterprise(false)}
                  />
                ) : (
                  <button
                    onClick={() => setShowJoinEnterprise(true)}
                    className="px-4 py-2 flex items-center bg-black text-white rounded-md hover:bg-gray-800"
                  >
                    <Key className="w-5 h-5 mr-2" />
                    Join with Enterprise Key
                  </button>
                )}
              </div>

              <div className="bg-white shadow rounded-lg p-6">
                <h2 className="text-xl font-semibold mb-4 flex items-center">
                  <Building className="mr-2" size={20} />
                  Create Enterprise Account
                </h2>

                <p className="text-gray-600 mb-4">
                  Set up an enterprise account to manage access for your team members.
                </p>

                <button
                  onClick={() => navigate('/enterprise/create')}
                  className="px-4 py-2 bg-black text-white rounded-md hover:bg-gray-800"
                >
                  Create Enterprise Account
                </button>
              </div>
            </div>
          );
        }

      case 'account-settings':
        return (
          <>
            <UpdateEmail />
            <UpdatePassword />
            {/* Sign Out Button */}
            <div className="bg-white shadow rounded-lg p-6">
              <button
                onClick={handleLogout}
                className="w-full flex items-center justify-center px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
              >
                <LogOut className="mr-2" size={18} />
                Sign Out
              </button>
            </div>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navbar component with sourcePage set to 'account' */}
      <Navbar sourcePage="account" />

      <div className="max-w-3xl mx-auto p-4">
        {/* Tab Navigation */}
        <div className="mb-6 border-b">
          <div className="flex flex-wrap -mb-px gap-y-3">
            <button
              onClick={() => setActiveTab('access-pass')}
              className={`pb-4 px-2 mr-3 md:mr-8 flex items-center ${
                activeTab === 'access-pass'
                  ? 'border-b-2 border-black text-black font-medium'
                  : 'text-gray-500'
              }`}
            >
              <CreditCard className="w-4 h-4 mr-2" />
              Access Pass
            </button>
            <button
              onClick={() => setActiveTab('usage')}
              className={`pb-4 px-2 mr-3 md:mr-8 flex items-center ${
                activeTab === 'usage'
                  ? 'border-b-2 border-black text-black font-medium'
                  : 'text-gray-500'
              }`}
            >
              <BarChart2 className="w-4 h-4 mr-2" />
              Usage
            </button>
            <button
              onClick={() => setActiveTab('shares')}
              className={`pb-4 px-2 mr-3 md:mr-8 flex items-center ${
                activeTab === 'shares'
                  ? 'border-b-2 border-black text-black font-medium'
                  : 'text-gray-500'
              }`}
            >
              <Share2 className="w-4 h-4 mr-2" />
              Shares
            </button>
            <button
              onClick={() => setActiveTab('enterprise')}
              className={`pb-4 px-2 mr-3 md:mr-8 flex items-center ${
                activeTab === 'enterprise'
                  ? 'border-b-2 border-black text-black font-medium'
                  : 'text-gray-500'
              }`}
            >
              <Building className="w-4 h-4 mr-2" />
              Enterprise
            </button>
            <button
              onClick={() => setActiveTab('account-settings')}
              className={`pb-4 px-2 mr-3 md:mr-8 flex items-center ${
                activeTab === 'account-settings'
                  ? 'border-b-2 border-black text-black font-medium'
                  : 'text-gray-500'
              }`}
            >
              <User className="w-4 h-4 mr-2" />
              Settings
            </button>
          </div>
        </div>

        {/* Tab Content */}
        {renderTabContent()}
      </div>
    </div>
  );
}

export default Account;